/**
 * The volume: a unit cube (back faces) carrying the DVR material, placed in the scene group in
 * mm with the pipeline frame conventions (theater/frame.ts). Mirrors VolumeRenderedObject's
 * uniform push (UpdateMatInternal) and the per-preset setters on VolumetricObject.
 */
import * as THREE from 'three';
import { MM_PER_UNIT, volumeCenterMm, volumeExtentMm, type VolumeGrid } from '@/theater/frame';
import { dvrFragmentShader, dvrVertexShader, MAX_CROSS_SECTIONS } from './dvrShader';
import { bakeTransferFunction, makeTfTexture, NEUTRAL_DOMAIN, updateTfTexture, type TfDomain, type TransferFunctionJson } from './tf';
import type { RenderMode } from './presets';
import { DISABLED_CUSP_PLANE, LEAFLET_DEFAULT_EMPHASIS, LEAFLET_DEFAULT_FILL, LEAFLET_DEFAULT_OPACITY, LEAFLET_REF_DENSITY } from './leaflets';
import { CLEAN_ROOT_LIGHTING_THRESHOLD } from './cleanRoot';

/** A bound clean-root set (VolumeRenderedObject.SetCleanRoot): the frame's clean volume, the
 * root region mask on the same uvw grid and the clean TF (the run's per-scan file repainted
 * with the inside-root palette, or the bundled clean cutaway TF). */
export interface CleanRootBinding {
  texture: THREE.Data3DTexture;
  region: THREE.Data3DTexture;
  tf: TransferFunctionJson;
}

/** A bound cusp compartment field (VascularShellCutout.SetCuspField). */
export interface CuspFieldBinding {
  /** RGBA: per-cusp boundary distance + deficiency mask. */
  texture: THREE.Data3DTexture;
  /** R8 complete geometric leaflet on the same grid, or null (the fill slider then clamps at 1). */
  full: THREE.Data3DTexture | null;
  dims: [number, number, number];
  distMaxMm: number;
  t0Mm: number;
  t1Mm: number;
}

/** The leaflet look the presets push (VascularShellCutout emphasis / fill / colour, the Clean Root tile's leaflet-set opacity). */
export interface LeafletLook {
  fill: number;
  emphasis: number;
  opacity: number;
  /** sRGB override, or null to paint with the TF's own tissue texel. */
  color: [number, number, number] | null;
}

export type CrossSectionType = 'plane' | 'boxIncl' | 'boxExcl' | 'sphereIncl' | 'sphereExcl';
const CROSS_TYPE_ID: Record<CrossSectionType, number> = {
  plane: 1,
  boxIncl: 2,
  boxExcl: 3,
  sphereIncl: 4,
  sphereExcl: 5,
};

export interface CrossSection {
  id: string;
  type: CrossSectionType;
  /** Tool object: its local space is where the test runs (plane clips local +z; box is ±0.5;
   * sphere radius 0.5). Positioned in scene mm (inside the scene group). */
  object: THREE.Object3D;
  enabled: boolean;
}

function makeNoiseTexture(size = 512): THREE.DataTexture {
  const data = new Uint8Array(size * size);
  for (let i = 0; i < data.length; i++) data[i] = Math.floor(Math.random() * 256);
  const tex = new THREE.DataTexture(data, size, size, THREE.RedFormat, THREE.UnsignedByteType);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

let sharedNoise: THREE.DataTexture | null = null;

export class VolumeObject {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly grid: VolumeGrid;
  readonly tfTexture: THREE.DataTexture;
  private tfJson: TransferFunctionJson;
  private tfDomain: TfDomain = { ...NEUTRAL_DOMAIN };
  private tfData: Float32Array;
  private crossSections: CrossSection[] = [];
  private defines: Record<string, string | number> = {};
  private _mode: RenderMode = 'dvr';
  private _lighting = false;
  private _cubic = false;
  private _cuspField = false;
  private _cleanRoot = false;
  private cleanTfTexture: THREE.DataTexture | null = null;
  private cleanTfJson: TransferFunctionJson | null = null;

  constructor(dataTexture: THREE.Data3DTexture, grid: VolumeGrid, tf: TransferFunctionJson) {
    this.grid = grid;
    this.tfJson = tf;
    this.tfData = bakeTransferFunction(tf);
    this.tfTexture = makeTfTexture(this.tfData);
    sharedNoise ??= makeNoiseTexture();
    const crossMats: THREE.Matrix4[] = [];
    const crossTypes: number[] = [];
    for (let i = 0; i < MAX_CROSS_SECTIONS; i++) {
      crossMats.push(new THREE.Matrix4());
      crossTypes.push(0);
    }
    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: dvrVertexShader,
      fragmentShader: dvrFragmentShader,
      uniforms: {
        uData: { value: dataTexture },
        uModelMatrix: { value: new THREE.Matrix4() },
        uTF: { value: this.tfTexture },
        uNoise: { value: sharedNoise },
        uTextureSize: { value: new THREE.Vector3(grid.dims[0], grid.dims[1], grid.dims[2]) },
        uCameraLocal: { value: new THREE.Vector3() },
        uCameraForwardLocal: { value: new THREE.Vector3(0, 0, -1) },
        uOrtho: { value: 0 },
        uMinVal: { value: 0.3 },
        uMaxVal: { value: 0.37 },
        uBoxOpacity: { value: 0.24 },
        uSamplingMultiplier: { value: 2 },
        uMinGradient: { value: 0.01 },
        uGradientOpacityModulation: { value: 0 },
        uLightStart: { value: 0.02 },
        uLightEnd: { value: 0.15 },
        uCameraClipDistance: { value: 0.25 },
        uDepthTintIntensity: { value: 0 },
        uDepthTintDistance: { value: 1 },
        uDepthTintNear: { value: new THREE.Color(1, 1, 1) },
        uDepthTintFar: { value: new THREE.Color(1, 1, 1) },
        uCrossCount: { value: 0 },
        uCrossMat: { value: crossMats },
        uCrossType: { value: crossTypes },
        uMaskEnabled: { value: 0 },
        uMask: { value: dataTexture },
        uMaskThreshold: { value: 0.5 },
        uCusp: { value: dataTexture },
        uCuspFull: { value: dataTexture },
        uCuspFullEnabled: { value: 0 },
        uCuspDistMaxMm: { value: 4 },
        uCuspT0: { value: 0.35 },
        uCuspT1: { value: 1 },
        uCuspPlane: { value: DISABLED_CUSP_PLANE.clone() },
        uCuspTexelSize: { value: new THREE.Vector3(1 / grid.dims[0], 1 / grid.dims[1], 1 / grid.dims[2]) },
        uLeafletFill: { value: LEAFLET_DEFAULT_FILL },
        uLeafletEmphasis: { value: LEAFLET_DEFAULT_EMPHASIS },
        uLeafletOpacity: { value: LEAFLET_DEFAULT_OPACITY },
        uLeafletRefDensity: { value: LEAFLET_REF_DENSITY },
        uLeafletColor: { value: new THREE.Color(1, 1, 1) },
        uLeafletColorMix: { value: 0 },
        uClean: { value: dataTexture },
        uCleanTF: { value: this.tfTexture },
        uRegion: { value: dataTexture },
        uCleanLightStart: { value: CLEAN_ROOT_LIGHTING_THRESHOLD[0] },
        uCleanLightEnd: { value: CLEAN_ROOT_LIGHTING_THRESHOLD[1] },
        uRawSetOpacity: { value: 1 },
        uCleanSetOpacity: { value: 1 },
      },
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.SrcAlphaFactor,
      blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    });
    this.material.defines = this.defines;
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), this.material);
    const extent = volumeExtentMm(grid);
    const center = volumeCenterMm(grid);
    this.mesh.scale.copy(extent);
    this.mesh.position.copy(center);
    this.mesh.renderOrder = 0;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'volume';
    this.applyDefines();
  }

  // ------------------------------------------------------------ per-frame
  /** Call before rendering with the camera that will draw this view. */
  updateCamera(camera: THREE.Camera): void {
    this.mesh.updateMatrixWorld();
    const inv = new THREE.Matrix4().copy(this.mesh.matrixWorld).invert();
    const u = this.material.uniforms;
    (u.uModelMatrix.value as THREE.Matrix4).copy(this.mesh.matrixWorld);
    (u.uCameraLocal.value as THREE.Vector3).copy(camera.position).applyMatrix4(inv);
    const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
    const normal = new THREE.Matrix3().getNormalMatrix(inv);
    (u.uCameraForwardLocal.value as THREE.Vector3).copy(fwd).applyMatrix3(normal).normalize();
    u.uOrtho.value = (camera as THREE.OrthographicCamera).isOrthographicCamera ? 1 : 0;
  }

  // ------------------------------------------------------------ setters
  setDataTexture(tex: THREE.Data3DTexture): void {
    this.material.uniforms.uData.value = tex;
  }

  setTransferFunction(tf: TransferFunctionJson): void {
    this.tfJson = tf;
    this.rebakeTf();
  }

  get transferFunction(): TransferFunctionJson {
    return this.tfJson;
  }

  get bakedTf(): Float32Array {
    return this.tfData;
  }

  /** The shift / stretch domain the texture was baked with (pivot = the window centre). */
  get domain(): TfDomain {
    return { ...this.tfDomain };
  }

  setDomain(shift: number, stretch: number): void {
    const w = this.visibilityWindow;
    this.tfDomain = { shift, scale: stretch, pivot: 0.5 * (w[0] + w[1]) };
    this.rebakeTf();
  }

  private rebakeTf(): void {
    this.tfData = bakeTransferFunction(this.tfJson, this.tfDomain);
    updateTfTexture(this.tfTexture, this.tfData);
  }

  get visibilityWindow(): [number, number] {
    return [this.material.uniforms.uMinVal.value as number, this.material.uniforms.uMaxVal.value as number];
  }

  setVisibilityWindow(min: number, max: number): void {
    this.material.uniforms.uMinVal.value = min;
    this.material.uniforms.uMaxVal.value = max;
    if (this.tfDomain.scale !== 1) this.setDomain(this.tfDomain.shift, this.tfDomain.scale);
  }

  setBoxOpacity(v: number): void {
    this.material.uniforms.uBoxOpacity.value = Math.min(1, Math.max(0, v));
  }

  setSamplingMultiplier(v: number): void {
    this.material.uniforms.uSamplingMultiplier.value = Math.max(0.05, v);
  }

  get samplingMultiplier(): number {
    return this.material.uniforms.uSamplingMultiplier.value as number;
  }

  setGradientLightingThreshold(start: number, end: number): void {
    this.material.uniforms.uLightStart.value = start;
    this.material.uniforms.uLightEnd.value = end;
  }

  setGradientVisibilityThreshold(v: number): void {
    this.material.uniforms.uMinGradient.value = v;
  }

  /** Camera clip in Unity units (1 = 100 mm), like the desktop slider. */
  setCameraClipDistance(v: number): void {
    this.material.uniforms.uCameraClipDistance.value = Math.max(0, v);
  }

  get cameraClipDistance(): number {
    return this.material.uniforms.uCameraClipDistance.value as number;
  }

  setDepthTint(intensity: number, distance: number, near: THREE.Color, far: THREE.Color): void {
    const u = this.material.uniforms;
    u.uDepthTintIntensity.value = intensity;
    u.uDepthTintDistance.value = Math.max(0.001, distance);
    (u.uDepthTintNear.value as THREE.Color).copy(near);
    (u.uDepthTintFar.value as THREE.Color).copy(far);
  }

  setLightingEnabled(on: boolean): void {
    this._lighting = on;
    this.applyDefines();
  }

  get lightingEnabled(): boolean {
    return this._lighting;
  }

  setCubicInterpolationEnabled(on: boolean): void {
    this._cubic = on;
    this.applyDefines();
  }

  get cubicInterpolationEnabled(): boolean {
    return this._cubic;
  }

  setRenderMode(mode: RenderMode): void {
    this._mode = mode;
    this.applyDefines();
  }

  get renderMode(): RenderMode {
    return this._mode;
  }

  setIsolationMask(tex: THREE.Data3DTexture | null, threshold = 0.5): void {
    const u = this.material.uniforms;
    u.uMaskEnabled.value = tex ? 1 : 0;
    u.uMask.value = tex ?? u.uData.value;
    u.uMaskThreshold.value = threshold;
  }

  // ------------------------------------------------------------ leaflet overlay
  /** Bind (or with null unbind) the frame's cusp field: the leaflet surface renders on top of
   * plain DVR (VascularShellCutout.SetCuspField + SetLeafletOverlay). The shader term compiles
   * in only while a field is bound. */
  setCuspField(binding: CuspFieldBinding | null): void {
    const u = this.material.uniforms;
    u.uCusp.value = binding?.texture ?? u.uData.value;
    u.uCuspFull.value = binding?.full ?? u.uData.value;
    u.uCuspFullEnabled.value = binding?.full ? 1 : 0;
    if (binding) {
      u.uCuspDistMaxMm.value = Math.max(0.001, binding.distMaxMm);
      u.uCuspT0.value = Math.max(0, binding.t0Mm);
      u.uCuspT1.value = Math.max(binding.t0Mm + 0.05, binding.t1Mm);
      (u.uCuspTexelSize.value as THREE.Vector3).set(1 / binding.dims[0], 1 / binding.dims[1], 1 / binding.dims[2]);
    }
    this._cuspField = !!binding;
    this.applyDefines();
  }

  get cuspFieldBound(): boolean {
    return this._cuspField;
  }

  /** The commissural cap-cutoff plane in uvw-scaled mm form (leaflets.ts cuspCapPlane); null disables the cutoff. */
  setCuspPlane(plane: THREE.Vector4 | null): void {
    (this.material.uniforms.uCuspPlane.value as THREE.Vector4).copy(plane ?? DISABLED_CUSP_PLANE);
  }

  setLeafletLook(look: Partial<LeafletLook>): void {
    const u = this.material.uniforms;
    if (look.fill !== undefined) u.uLeafletFill.value = Math.min(2, Math.max(0, look.fill));
    if (look.emphasis !== undefined) u.uLeafletEmphasis.value = Math.min(1, Math.max(0, look.emphasis));
    if (look.opacity !== undefined) u.uLeafletOpacity.value = Math.min(1, Math.max(0, look.opacity));
    if (look.color !== undefined) {
      // Material.SetColor linearises an authored colour on the desktop; the TF texels are linear too.
      if (look.color) (u.uLeafletColor.value as THREE.Color).setRGB(look.color[0], look.color[1], look.color[2], THREE.SRGBColorSpace);
      u.uLeafletColorMix.value = look.color ? 1 : 0;
    }
  }

  get leafletLook(): LeafletLook {
    const u = this.material.uniforms;
    return {
      fill: u.uLeafletFill.value as number,
      emphasis: u.uLeafletEmphasis.value as number,
      opacity: u.uLeafletOpacity.value as number,
      color: (u.uLeafletColorMix.value as number) > 0.5 ? ((u.uLeafletColor.value as THREE.Color).clone().convertLinearToSRGB().toArray() as [number, number, number]) : null,
    };
  }

  // ------------------------------------------------------------ clean root
  /** Bind (or with null unbind) the clean-root set: the bound (raw) volume and TF are untouched,
   * so an outside view is exactly the Cardiac VR look; rays the clip opens inside the root
   * region render the clean volume with the clean TF (VolumeRenderedObject.SetCleanRoot /
   * ClearCleanRoot). The term compiles in only while a set is bound. */
  setCleanRoot(binding: CleanRootBinding | null): void {
    const u = this.material.uniforms;
    if (binding) {
      const baked = bakeTransferFunction(binding.tf);
      if (this.cleanTfTexture) updateTfTexture(this.cleanTfTexture, baked);
      else this.cleanTfTexture = makeTfTexture(baked);
      this.cleanTfJson = binding.tf;
      u.uClean.value = binding.texture;
      u.uRegion.value = binding.region;
      u.uCleanTF.value = this.cleanTfTexture;
    } else {
      this.cleanTfJson = null;
      u.uClean.value = u.uData.value;
      u.uRegion.value = u.uData.value;
      u.uCleanTF.value = this.tfTexture;
    }
    this._cleanRoot = !!binding;
    this.applyDefines();
  }

  get cleanRootBound(): boolean {
    return this._cleanRoot;
  }

  /** The clean set's transfer function while clean-root mode is up, else null. */
  get cleanRootTransferFunction(): TransferFunctionJson | null {
    return this.cleanTfJson;
  }

  /** The clean-root tile's per-set transparency sliders (raw / clean); (1, 1) off the tile. */
  setRenderSetOpacities(raw: number, clean: number): void {
    const u = this.material.uniforms;
    u.uRawSetOpacity.value = Math.min(1, Math.max(0, raw));
    u.uCleanSetOpacity.value = Math.min(1, Math.max(0, clean));
  }

  // ------------------------------------------------------------ cross sections
  addCrossSection(cs: CrossSection): void {
    this.crossSections = this.crossSections.filter((c) => c.id !== cs.id);
    this.crossSections.push(cs);
    this.refreshCrossSections();
  }

  removeCrossSection(id: string): void {
    this.crossSections = this.crossSections.filter((c) => c.id !== id);
    this.refreshCrossSections();
  }

  getCrossSection(id: string): CrossSection | undefined {
    return this.crossSections.find((c) => c.id === id);
  }

  /** CrossSectionManager.RefreshCrossSections: matrix = tool.worldToLocal * container.localToWorld,
   * applied to container-local centred space (uvw - 0.5). */
  refreshCrossSections(): void {
    const active = this.crossSections.filter((c) => c.enabled).slice(0, MAX_CROSS_SECTIONS);
    const u = this.material.uniforms;
    const mats = u.uCrossMat.value as THREE.Matrix4[];
    const types = u.uCrossType.value as number[];
    this.mesh.updateMatrixWorld();
    for (let i = 0; i < MAX_CROSS_SECTIONS; i++) {
      const cs = active[i];
      if (!cs) {
        types[i] = 0;
        continue;
      }
      cs.object.updateMatrixWorld();
      const toolInv = new THREE.Matrix4().copy(cs.object.matrixWorld).invert();
      mats[i].multiplyMatrices(toolInv, this.mesh.matrixWorld);
      types[i] = CROSS_TYPE_ID[cs.type];
    }
    u.uCrossCount.value = active.length;
    const want = active.length > 0;
    if (want !== 'CROSS_SECTION_ON' in this.defines) {
      if (want) this.defines.CROSS_SECTION_ON = 1;
      else delete this.defines.CROSS_SECTION_ON;
      this.material.needsUpdate = true;
    }
  }

  // ------------------------------------------------------------ helpers
  /** Extent of the box in Unity units, as VolumeDataset.scale. */
  get datasetScale(): THREE.Vector3 {
    return volumeExtentMm(this.grid).multiplyScalar(1 / MM_PER_UNIT);
  }

  private applyDefines(): void {
    const d: Record<string, string | number> = {};
    if (this._mode === 'mip') d.MODE_MIP = 1;
    else if (this._mode === 'iso') d.MODE_SURF = 1;
    else d.MODE_DVR = 1;
    if (this._lighting) d.LIGHTING_ON = 1;
    if (this._cubic) d.CUBIC_INTERPOLATION_ON = 1;
    if (this._cuspField) d.CUSP_FIELD_ON = 1;
    if (this._cleanRoot) d.CLEAN_ROOT_ON = 1;
    if ('CROSS_SECTION_ON' in this.defines) d.CROSS_SECTION_ON = 1;
    const changed = JSON.stringify(d) !== JSON.stringify(this.defines);
    this.defines = d;
    this.material.defines = d;
    if (changed) this.material.needsUpdate = true;
  }

  dispose(): void {
    this.material.dispose();
    this.mesh.geometry.dispose();
    this.tfTexture.dispose();
    this.cleanTfTexture?.dispose();
  }
}
